import { Dispatch, SetStateAction } from 'react';
import { ApiData } from './apiService';

interface EditRowData {
    order_table: string;
    order_data: any;
}

interface UpdateRow {
    id: number;
    [key: string]: string | number;  // Allow any string key with string or number values
}

interface SocketDeleteData {
    deleted_id: number[]; // [array of ids]
    table_name: string;
}

// TODO: get tables from someplace else so we don't have to manually add them here to enable socket updates
const apiTables = {
    'main_table': 'main_table',
    'lease_coordinates': 'lease_coordinates',
    'market_coordinates': 'market_coordinates',
    'pending_approvals': 'pending_approvals',
    'pumper_contact': 'pumper_contact',
    'components_price': 'components_price',
    'market_discounts': 'market_discounts',
    'name_corrections': 'name_corrections',
    'trucking_rates': 'trucking_rates',
    'producer_discounts': 'producer_discounts',
    'components_pricing': 'components_pricing',
    'producer_pricing': 'producer_pricing',
    'customer_reports': 'customer_reports',
    'mobile_req_mailing_list': 'mobile_req_mailing_list',
    'producer_groups': 'producer_groups',
    'producer_definition': 'producer_definition',
    'entity_definition': 'entity_definition',
    'entity_producer_history': 'entity_producer_history',
    'schedule_table': 'schedule_table',
    'carrier_definition': 'carrier_definition',
    'carrier_pricing': 'carrier_pricing',
    'carrier_pricing_producer': 'carrier_pricing_producer',

    'crude_main': 'crude_main',
    'crude_main_pickup': 'crude_main_pickup',
    'crude_main_delivery': 'crude_main_delivery',
    'crude_tanks_definition': 'crude_tanks_definition',
    'warning_adjustment': 'warning_adjustment',
    'table_presets': 'table_presets',

    'carrier_cptransport': 'carrier_cptransport',
	'carrier_diamondb': 'carrier_diamondb',
	'carrier_endeavor': 'carrier_endeavor',
	'carrier_guardian': 'carrier_guardian',
	'carrier_heartriver': 'carrier_heartriver',
	'carrier_heartrivernd': 'carrier_heartrivernd',
	'carrier_penley': 'carrier_penley',
	'carrier_southhaven': 'carrier_southhaven',
	'carrier_texon': 'carrier_texon',
    'carrier_emmaus': 'carrier_emmaus',
    'carrier_opa': 'carrier_opa',
	'carrier_troy': 'carrier_troy',
	'automate_scheduling': 'automate_scheduling',
	'vac_schedule_table_vs': 'vac_schedule_table_vs',
	'oauth_clients': 'oauth_clients',

} as const;

export function socketDeleteRow ( 
    data: SocketDeleteData, 
    setApiData: React.Dispatch<React.SetStateAction<any>>
) {
    const deletedIdsArray = data.deleted_id;
    const tableName = data.table_name;

    const deletedIds = deletedIdsArray.map((id: number) => id);

    setApiData((prevData: any) => {
        // If no previous data or table doesn't exist, return as-is.
        if (!prevData || !prevData[tableName]) return prevData;
    
        return {
            ...prevData,
            [tableName]: prevData[tableName].filter((row: any) => !deletedIds.includes(row.id)),
        };
    });
}

export const socketEditRow = (
    data: EditRowData,
    setApiData: Dispatch<SetStateAction<ApiData | null>>
) => {

    const table_name = data.order_table;
    const row_data = data.order_data;
    console.log('received edit:', table_name, row_data)

    if (row_data) {
        console.log('contains data:', table_name, row_data)
    }

    if (!row_data) {
        console.error('No row data provided for edit operation');
        return;
    }

    const dataToProcess = Array.isArray(row_data) ? row_data[0] : row_data;

    // oauth_clients uses client_id (UUID) as its PK — handle before the integer id guard
    if (table_name === 'oauth_clients') {
        const dataToUpdate = { ...dataToProcess };
        if (!dataToUpdate?.client_id) return;
        // MySQL returns JSON columns as raw strings — parse them so the UI always
        // receives the correct types (same normalisation as fetchOAuthClients).
        if (typeof dataToUpdate.allowed_ips === 'string') {
            try { dataToUpdate.allowed_ips = JSON.parse(dataToUpdate.allowed_ips); }
            catch { dataToUpdate.allowed_ips = null; }
        }
        if (typeof dataToUpdate.scopes === 'string') {
            try { dataToUpdate.scopes = JSON.parse(dataToUpdate.scopes); }
            catch { dataToUpdate.scopes = []; }
        }
        setApiData(prevData => {
            if (!prevData || !Array.isArray(prevData['oauth_clients'])) return prevData;
            return {
                ...prevData,
                oauth_clients: (prevData['oauth_clients'] as any[]).map((row: any) =>
                    String(row.client_id) === String(dataToUpdate.client_id)
                        ? { ...row, ...dataToUpdate }
                        : row
                ),
            };
        });
        return;
    }

    if (!dataToProcess || !dataToProcess.id) {
        console.error('No valid data with ID provided for edit operation:', row_data);
        return;
    }
    
    if (table_name === 'schedule_table' || table_name === 'bottoms_schedule_table' || table_name === 'ngl_schedule_table') {
        setApiData(prevData => {
            if (!prevData || !prevData[table_name] || !Array.isArray(prevData[table_name])) {
                console.error(`Table ${table_name} not found or not an array in prevData`);
                return prevData;
            }
            
            const dataToUpdate = Array.isArray(row_data) ? row_data[0] : row_data;
            
            const updatedTable = prevData[table_name].map(row => {
                if (row && row.id && String(row.id) === String(dataToUpdate.id)) {
                    console.log('Updating row:', row, 'with:', dataToUpdate);
                    return { ...row, ...dataToUpdate };
                }
                return row;
            });

            return {
                ...prevData,
                [table_name]: updatedTable
            };
        });
    } 
    // For all other tables
    else if (table_name in apiTables) {

        type ApiTableKeys = keyof typeof apiTables;

        setApiData(prevData => {
            if (!prevData) return null;

            const dataToUpdate = Array.isArray(row_data) ? row_data[0] : row_data;
            const tableKey = apiTables[table_name as ApiTableKeys];
            
            if (!Array.isArray(prevData[tableKey])) {
                // Initialize empty array if undefined
                return {
                    ...prevData,
                    [tableKey]: [dataToUpdate]
                };
            }

            const updatedTable = prevData[tableKey].map(row => {
                if (String(row.id) === String(dataToUpdate.id)) {
                    return { ...row, ...dataToUpdate };
                }
                return row;
            });

            return {
                ...prevData,
                [tableKey]: updatedTable
            };
        });
    }
};  

export const socketBigEdit = (
    data: {
        order_table: string,
        order_columns: string[], // Changed from order_column to order_columns array
        order_data: Array<UpdateRow>
    },
    setApiData: Dispatch<SetStateAction<ApiData | null>>
) => {
    const { order_table: table_name, order_columns, order_data } = data;
    console.log('received big edit:', table_name, order_columns, order_data)

    if (table_name === 'schedule_table' || table_name === 'bottoms_schedule_table' || table_name === 'ngl_schedule_table') {
        setApiData(prevData => {
            if (!prevData) return null;

            const updatedTable = prevData[table_name].map(row => {
                // Find if this row should be updated
                const updateInfo = order_data.find(updateRow => String(updateRow.id) === String(row.id));
    
                if (updateInfo) {
                    // Create an object with all columns to update
                    const update = (order_columns || []).reduce((acc, column) => ({
                        ...acc,
                        [column]: updateInfo[column]
                    }), {});
                    
                    return { ...row, ...update };
                }
                return row;
            });

            return {
                ...prevData,
                [table_name]: updatedTable
            };
        });
    }
    // For all other tables
    else if (table_name in apiTables) {
        type ApiTableKeys = keyof typeof apiTables;

        setApiData(prevData => {
            if (!prevData) return null;

            const tableKey = apiTables[table_name as ApiTableKeys];
            
            const updatedTable = prevData[tableKey].map(row => {
                // Find if this row should be updated
                const updateInfo = order_data.find(updateRow => String(updateRow.id) === String(row.id));
                
                if (updateInfo) {
                    // Create an object with all columns to update
                    const update = (order_columns || []).reduce((acc, column) => ({
                        ...acc,
                        [column]: updateInfo[column]
                    }), {});
                    
                    return { ...row, ...update };
                }
                return row;
            });

            return {
                ...prevData,
                [tableKey]: updatedTable
            };
        });
    }
};

interface TableUpdateData {
    order_table: string;
    order_data: Array<{
        id: number | string;
        [key: string]: any;
    }>;
}

type ApiTableKeys = keyof typeof apiTables;

export const socketTableUpdate = (
    data: TableUpdateData,
    setApiData: React.Dispatch<React.SetStateAction<ApiData | null>>
) => {
    const { order_table: tableName, order_data: rows } = data;
    
    setApiData(prevData => {
        if (!prevData) return null;
        
        // Determine the correct table key
        const tableKey = tableName in apiTables 
            ? apiTables[tableName as ApiTableKeys] 
            : tableName;
      
        // Get the current table data
        const currentTable = prevData[tableKey as keyof typeof prevData];
        if (!Array.isArray(currentTable)) return prevData;
    
        const updatedTable = [...currentTable];
    
        rows.forEach(newRow => {
            const index = updatedTable.findIndex(existingRow => 
                String(existingRow.id) === String(newRow.id)
            );
            if (index !== -1) {
                // Update existing row and assert the merged object matches the element type
                updatedTable[index] = { 
                    ...updatedTable[index], 
                    ...newRow 
                } as typeof updatedTable[number];
            } else {
                // Push new row with a type assertion
                updatedTable.push(newRow as typeof updatedTable[number]);
            }
        });
  
        return {
            ...prevData,
            [tableKey]: updatedTable,
        };
    });
};
  